import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  SectionFrame,
  Marquee,
  Stagger,
  StaggerItem,
  Reveal,
  AnimatedNumber,
  TiltCard,
  Sheen,
  FlowLine,
} from '@/components/landing/kit';
import {
  fadeInUp,
  staggerSlow,
  staggerContainer,
} from '@/components/landing/animations';

const easeEditorial = [0.2, 0.6, 0.2, 1] as const;

/**
 * 職人 / The Living Ledger — HERO.
 *
 * The "live close at 08:00" terminal: the entire value proposition held
 * in one still frame, then awoken ONCE. The hero is the page's opening,
 * so it owns its OWN closing hairline (every other section opens with a
 * drawLine rule). Above the fold, so the LEFT copy column animates on
 * LOAD (initial/animate) while the RIGHT mock's internal rows + the
 * closing rule animate on inView. Everything plays once and holds still.
 */
export function HeroSection() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  // Load-choreography variants for the above-fold LEFT column. We can't
  // use the kit's whileInView primitives here because the hero must paint
  // its cascade on first load, not on scroll. Reduced motion collapses to
  // a plain opacity fade with no transform.
  const loadGroup = reduce
    ? undefined
    : staggerSlow;
  const loadItem = reduce
    ? { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } }
    : fadeInUp;

  return (
    <section className="relative isolate overflow-hidden">
      <SectionFrame ambient="grid-faint" rule={false} id="hero">
        {/* 1) MASTHEAD TICKER — the page's only permitted idle loop. */}
        <div className="relative border-y border-border bg-ink-50/60 dark:bg-ink-950/40 veil-bottom">
          <Marquee durationSec={90} className="h-9">
            <TickerStrip intro={t('lp.hero.ticker_intro', { defaultValue: 'Close status —' })} />
          </Marquee>
        </div>

        {/* 2) BROADSHEET SPLIT */}
        <div className="container relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* LEFT — copy column, reading-order spine (load cascade). */}
            <motion.div
              className="lg:col-span-7"
              variants={loadGroup}
              initial="hidden"
              animate="visible"
            >
              {/* a. Eyebrow pill — leads with a live emerald dot. */}
              <motion.div variants={loadItem}>
                <span className="inline-flex items-center gap-2 rounded-full pill-slate px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
                  <span className="relative inline-flex h-1.5 w-1.5" aria-hidden="true">
                    {!reduce && (
                      <motion.span
                        className="absolute inline-flex h-full w-full rounded-full bg-emerald-500"
                        animate={{ scale: [1, 2.2, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
                      />
                    )}
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  <span>{t('lp.hero.eyebrow', { defaultValue: 'Close in progress · live' })}</span>
                  <span className="hidden sm:inline num text-muted-foreground/80">·</span>
                  <span className="hidden sm:inline num text-muted-foreground/80 tracking-[0.12em]">
                    {t('lp.hero.eyebrow_period', { defaultValue: 'FY2025 · May close' })}
                  </span>
                </span>
              </motion.div>

              {/* b. Serif headline — the hero figure settles LAST, then the
                  underline draws under the single load-bearing word. */}
              <motion.h1
                variants={loadItem}
                className="display serif-jp text-[2.7rem] sm:text-5xl md:text-[3.6rem] lg:text-[4.5rem] font-medium tracking-tight leading-[0.96] text-foreground text-balance mt-7"
              >
                {t('lp.hero.headline_pre', { defaultValue: 'Close the books in' })}{' '}
                <span
                  role="img"
                  aria-label="6 hours"
                  className="inline-block align-baseline"
                >
                  <span aria-hidden="true">
                    <AnimatedNumber
                      value={6}
                      durationMs={1500}
                      className="figure-hero text-foreground inline-block min-w-[1.1em] text-center align-baseline tabular-col"
                    />
                  </span>
                </span>{' '}
                <span className="text-[0.6em] text-muted-foreground font-normal align-baseline">
                  {t('lp.hero.headline_unit', { defaultValue: 'hours,' })}
                </span>{' '}
                <span className="text-muted-foreground">
                  {t('lp.hero.headline_contrast', { defaultValue: 'not 6 days.' })}
                </span>
                <span className="block mt-1 text-muted-foreground">
                  {t('lp.hero.headline_line2_pre', { defaultValue: 'AI you can' })}{' '}
                  <span className="text-foreground">
                    {/* The single underline-emerald anchor draws a beat AFTER
                        the count-up settles. */}
                    <Reveal
                      as="span"
                      variant="drawLine"
                      delay={reduce ? 0 : 1.6}
                      className="underline-emerald inline-block origin-left"
                    >
                      {t('lp.hero.headline_anchor', { defaultValue: 'sign off' })}
                    </Reveal>
                  </span>{' '}
                  {t('lp.hero.headline_line2_post', { defaultValue: 'on.' })}
                </span>
              </motion.h1>

              {/* c. Sub-paragraph — the provenance promise. */}
              <motion.p
                variants={loadItem}
                className="mt-7 max-w-[34rem] text-base md:text-[1.05rem] text-muted-foreground leading-relaxed text-pretty"
              >
                {t('lp.hero.sub', {
                  defaultValue:
                    'CadencEZ runs the consolidated close across every entity and currency — then explains each variance in plain language, citing the exact ledger rows behind it. Click any number and trace it to its source. Nothing to take on faith.',
                })}
              </motion.p>

              {/* d. CTA row — hero primary stays ink (shadow-emerald is
                  reserved for pricing / final CTA). */}
              <motion.div
                variants={loadItem}
                className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3"
              >
                <Link
                  to="/signup"
                  aria-label={t('lp.hero.primary_cta_aria', {
                    defaultValue: 'Start free — create your CadencEZ workspace',
                  })}
                  className="inline-flex h-11 items-center gap-2 px-6 rounded-md bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
                >
                  {t('lp.hero.primary_cta', { defaultValue: 'Start free' })}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex h-11 items-center gap-2 px-6 rounded-md border border-border text-sm font-semibold hover:bg-ink-100/80 transition-colors"
                >
                  {t('lp.hero.secondary_cta', { defaultValue: 'See pricing' })}
                </Link>
                <span className="text-xs text-muted-foreground sm:ml-1">
                  {t('lp.hero.free_note', {
                    defaultValue: 'No card required · 30-day full-feature trial, no lockout',
                  })}
                </span>
              </motion.div>

              {/* e. Stat strip — three figures, each settling LAST in its
                  cell over its label. Inner stagger cascades L→R. */}
              <motion.div variants={loadItem} className="mt-12">
                <motion.div
                  className="grid grid-cols-3 max-w-lg md:max-w-lg"
                  variants={reduce ? undefined : staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-80px' }}
                >
                  <StatCell
                    border={false}
                    value={
                      <AnimatedNumber
                        value={14}
                        durationMs={1300}
                        className="num-display text-2xl md:text-3xl font-semibold text-foreground tabular-col"
                      />
                    }
                    label={t('lp.hero.stat_locales_label', { defaultValue: 'Languages, shipped' })}
                  />
                  <StatCell
                    border
                    value={
                      <AnimatedNumber
                        value={250}
                        durationMs={1400}
                        suffix="K"
                        className="num-display text-2xl md:text-3xl font-semibold text-foreground tabular-col"
                      />
                    }
                    label={t('lp.hero.stat_scale_label', { defaultValue: 'Facts · sub-second reads' })}
                  />
                  <StatCell
                    border
                    value={
                      <AnimatedNumber
                        value={30}
                        durationMs={1300}
                        className="num-display text-2xl md:text-3xl font-semibold text-foreground tabular-col"
                      />
                    }
                    label={t('lp.hero.stat_trial_label', { defaultValue: 'Day free trial' })}
                  />
                </motion.div>
              </motion.div>
            </motion.div>

            {/* RIGHT — the signature artifact: the live BvA ledger. */}
            <div className="lg:col-span-5">
              <TiltCard max={4} perspective={1200}>
                <Sheen trigger="inView" durationMs={1100} className="rounded-lg">
                  <HeroProductMock />
                </Sheen>
              </TiltCard>
            </div>
          </div>
        </div>

        {/* 3) CLOSING RULE — hands off to the proof band. */}
        <Reveal variant="drawLine" className="rule mx-auto max-w-7xl origin-left" />
      </SectionFrame>
    </section>
  );
}

/* ── Masthead ticker content ───────────────────────────────────────── */

function TickerStrip({ intro }: { intro: string }) {
  // Real close-status figures. Each item is mono/tabular and separated by
  // a faint hairline + dot. Near-subliminal; the page's ambient pulse.
  const items = [
    intro,
    'FY2025 · May close · as of 08:00',
    '7 entities consolidated',
    '12 FX pairs eliminated',
    '248,910 facts reconciled',
    'p95 read 612ms',
    '14 locales',
    'close ETA 05:41:00',
    'JPY · USD · EUR · GBP · SGD',
    '2 facts cited / variance',
  ];
  return (
    <div className="flex items-center h-9">
      {items.map((it, i) => (
        <span key={i} className="flex items-center whitespace-nowrap">
          <span className="num tabular-nums text-[11px] text-muted-foreground px-4">
            {it}
          </span>
          <span aria-hidden="true" className="text-muted-foreground/40">·</span>
        </span>
      ))}
    </div>
  );
}

/* ── Stat strip cell ───────────────────────────────────────────────── */

function StatCell({
  value,
  label,
  border,
}: {
  value: React.ReactNode;
  label: string;
  border: boolean;
}) {
  return (
    <StaggerItem
      className={
        'flex flex-col py-1 ' +
        (border ? 'border-l border-border/80 pl-5' : 'pr-5')
      }
    >
      {value}
      <span className="mt-1 text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
    </StaggerItem>
  );
}

/* ── HeroProductMock — the live BvA variance ledger ────────────────── */

type Row = {
  code: string;
  label: string;
  bud: number;
  act: number;
  tone: 'warn' | 'good';
  /** Marks the EU-OPEX overage row that gets the single warn cell-flash. */
  warnFlash?: boolean;
  /** Marks the ¥4.1M source row the citation FlowLine threads up to. */
  cited?: boolean;
};

/**
 * The editorial variance ledger, kit-driven. A printed-report "page":
 *  - header strip (period + as-of stamp + Live pill-emerald)
 *  - 5-row BvA variance table (mono tabular, rows cascade + count-up)
 *  - AI-commentary footer carrying the [fact:12390] citation chip
 *  - a FlowLine threading the chip UP to the ¥4.1M source row, whose
 *    cited cell then tints emerald (the single lit emerald moment)
 *  - JP-locale hanko 承認 pressing once
 *
 * One-emerald discipline: pill-emerald Live chip, citation chip, FlowLine
 * dot and cited-cell tint all share the ONE accent family. warn-red fires
 * once (4100 row) sequenced BEFORE the emerald citation resolves.
 */
function HeroProductMock() {
  const { t, i18n } = useTranslation();
  const reduce = useReducedMotion();
  const isJa = (i18n.language || 'en').startsWith('ja');

  const rows: Row[] = [
    { code: '4100', label: isJa ? '欧州OPEX' : 'EU OPEX', bud: 39_500_000, act: 48_200_000, tone: 'warn', warnFlash: true, cited: true },
    { code: '5200', label: isJa ? 'APAC 売上' : 'APAC sales', bud: 128_000_000, act: 146_500_000, tone: 'good' },
    { code: '7100', label: isJa ? '人件費' : 'Headcount', bud: 92_000_000, act: 99_100_000, tone: 'warn' },
    { code: '8100', label: isJa ? 'マーケ' : 'Marketing', bud: 22_000_000, act: 14_800_000, tone: 'good' },
    { code: '9300', label: isJa ? '為替差損益' : 'FX', bud: 0, act: -3_200_000, tone: 'good' },
  ];

  const fmtYen = (n: number) => {
    if (n === 0) return '—';
    const sign = n < 0 ? '−' : '';
    const m = Math.abs(n) / 1_000_000;
    return `${sign}¥${m.toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  };

  const pct = (b: number, a: number) => (b === 0 ? null : ((a - b) / b) * 100);

  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: reduce ? 0.2 : 0.65, ease: easeEditorial }}
      className="relative bg-card text-foreground shadow-editorial border border-border rounded-lg overflow-hidden"
    >
      {/* Header strip — period + as-of stamp + Live pill-emerald. */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3 bg-ink-50/70 dark:bg-ink-900/40">
        <div className="flex items-baseline gap-3">
          <p className="display text-[15px] font-semibold tracking-tight">
            {t('lp.hero.mock_title', { defaultValue: 'Budget vs. Actual — Consolidated' })}
          </p>
          <span className="num text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            {t('lp.hero.mock_period', { defaultValue: 'FY2025 · May · as of 08:00' })}
          </span>
        </div>
        <span className="pill-emerald rounded-md px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.14em]">
          {t('lp.hero.mock_live', { defaultValue: 'Live' })}
        </span>
      </div>

      {/* Variance table — rows cascade top→bottom and rule in L→R. */}
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="border-b border-border/80 bg-ink-50/40 dark:bg-ink-950/30">
            <th className="text-left px-5 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('lp.hero.mock_th_account', { defaultValue: 'Account' })}
            </th>
            <th className="text-right px-3 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('lp.hero.mock_th_budget', { defaultValue: 'Budget' })}
            </th>
            <th className="text-right px-3 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('lp.hero.mock_th_actual', { defaultValue: 'Actual' })}
            </th>
            <th className="text-right px-5 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t('lp.hero.mock_th_delta', { defaultValue: 'Δ' })}
            </th>
          </tr>
        </thead>
        <Stagger as="tbody" className="divide-y divide-border/60">
          {rows.map((r) => {
            const p = pct(r.bud, r.act);
            const tone =
              p === null
                ? 'text-muted-foreground'
                : r.tone === 'warn'
                  ? 'text-warn-600'
                  : 'text-emerald-700';
            return (
              <StaggerItem
                as="tr"
                key={r.code}
                className="hover:bg-ink-50/50 dark:hover:bg-ink-900/30 transition-colors"
              >
                <td className="px-5 py-2.5">
                  <div className="flex items-baseline gap-2">
                    <span className="num text-muted-foreground text-[10.5px]">{r.code}</span>
                    <span className="text-foreground/95">{r.label}</span>
                  </div>
                </td>
                <td className="num text-right px-3 py-2.5 text-foreground/75">{fmtYen(r.bud)}</td>
                <td className="num text-right px-3 py-2.5 text-foreground">
                  {/* The warn overage row gets the single sanctioned cell-flash,
                      sequenced BEFORE the emerald citation resolves. The cited
                      ¥4.1M-driver row tints emerald LAST (the one lit emerald
                      moment of the viewport). */}
                  <motion.span
                    className={
                      (r.warnFlash && !reduce ? 'cell-warn-flash ' : '') +
                      'inline-block rounded px-1 -mx-1'
                    }
                    initial={false}
                    whileInView={
                      r.cited && !reduce
                        ? { backgroundColor: 'rgba(16,185,129,0.14)', color: '#047857' }
                        : undefined
                    }
                    viewport={{ once: true, margin: '-80px' }}
                    transition={
                      r.cited && !reduce
                        ? { duration: 0.2, delay: 2.0, ease: easeEditorial }
                        : undefined
                    }
                  >
                    {fmtYen(r.act)}
                  </motion.span>
                </td>
                <td className={`num text-right px-5 py-2.5 font-semibold tabular-col ${tone}`}>
                  {p === null ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1)}%`}
                </td>
              </StaggerItem>
            );
          })}
        </Stagger>
      </table>

      {/* Citation FlowLine — threads the [fact:12390] chip UP to the ¥4.1M
          source row with one traveling emerald dot that arrives and STOPS.
          aria-hidden decoration; the chip below is the real affordance. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <FlowLine
          d="M 80 78 C 80 60, 70 52, 58 46 C 46 40, 40 36, 40 30"
          dot
          dotColor="emerald"
          strokeClassName="stroke-emerald-600"
          durationMs={700}
          delay={reduce ? 0 : 1.4}
          viewBox="0 0 100 100"
          className="absolute inset-0 h-full w-full opacity-70"
        />
      </div>

      {/* AI commentary footer — fades in, carrying the citation chip. */}
      <motion.div
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
        whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: reduce ? 0.2 : 0.5, delay: reduce ? 0 : 1.1, ease: easeEditorial }}
        className="relative border-t border-border px-5 py-4 bg-ink-50/40 dark:bg-ink-950/20"
      >
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="num text-[10px] uppercase tracking-[0.16em] text-emerald-700">
            ✦ {t('lp.hero.mock_ai_label', { defaultValue: 'AI commentary' })}
          </span>
          <span className="num text-[9.5px] text-muted-foreground">
            {t('lp.hero.mock_ai_meta', { defaultValue: 'CadencEZ AI · 2 facts cited' })}
          </span>
        </div>
        <p className="text-[12px] leading-relaxed text-foreground/85">
          <AiBody
            body={t('lp.hero.mock_ai_body', {
              defaultValue:
                'EU OPEX is ¥8.7M over budget. A one-off Berlin office fit-out [fact:12390] accounts for ¥4.1M; the rest is recurring headcount. Trace every figure to its source rows.',
            })}
            citationLabel={t('lp.hero.mock_citation', { defaultValue: 'fact:12390' })}
          />
        </p>
      </motion.div>

      {/* JP-locale hanko — presses once and stays. aria-hidden decoration. */}
      {isJa && (
        <motion.div
          aria-hidden="true"
          initial={reduce ? { opacity: 0.18, scale: 1 } : { opacity: 0, scale: 1.08 }}
          whileInView={{ opacity: 0.18, scale: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: reduce ? 0 : 0.45, delay: reduce ? 0 : 0.9, ease: easeEditorial }}
          className="absolute right-4 top-16 rotate-[7deg] select-none"
          style={{
            color: 'hsl(var(--destructive))',
            border: '2px solid currentColor',
            borderRadius: '9999px',
            width: '3.4rem',
            height: '3.4rem',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Noto Serif JP", serif',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '-0.04em',
            lineHeight: 1,
          }}
        >
          {t('lp.hero.mock_hanko', { defaultValue: '承認' })}
        </motion.div>
      )}
    </motion.div>
  );
}

/**
 * Renders the AI body text, replacing the inline [fact:NNNNN] token with a
 * real, focusable .citation-link chip (the provenance affordance the
 * FlowLine threads to). Falls back gracefully if the token is absent.
 */
function AiBody({ body, citationLabel }: { body: string; citationLabel: string }) {
  const token = /\[fact:(\d+)\]/;
  const match = body.match(token);
  if (!match) return <>{body}</>;
  const [before, after] = body.split(match[0]);
  return (
    <>
      {before}
      <a
        href={`#${citationLabel}`}
        className="citation-link num text-[10.5px]"
        onClick={(e) => e.preventDefault()}
      >
        [{citationLabel}]
      </a>
      {after}
    </>
  );
}
